import React from 'react';
import { ImageElement, TemplateElement, VideoElement } from '@bynder-studio/render-core';
import { Button, Inspector, List, Thumbnail, token } from '@bynder/design-system';
import { IconBuildShape, IconSync, IconTextField, IconMusicLibrary } from '@bynder/icons';
import { TextElement } from 'packages/pages/design/components/TextElement';
import { useShotItem } from 'packages/pages/design/sidebar/shots/useShotItem';
import MediaElement from 'packages/pages/design/components/MediaElement';
import ShapeElement from 'packages/pages/design/components/ShapeElement';
import useContentPreviewUrl from 'packages/hooks/useContentPreviewUrl';
import useObjectUrl from 'packages/hooks/useObjectUrl';
import { ShotElementType } from 'packages/pages/design/sidebar/types';

type Props = {
    isLast: boolean;
    shotElement: TemplateElement;
    isToolbarDisabled: boolean;
    isLoading: boolean;
    isDisabledByReview: boolean;
};

const ShotItem = ({ isLast, shotElement, isToolbarDisabled, isLoading, isDisabledByReview }: Props) => {
    const {
        id,
        type,
        isTextElement,
        isImageElement,
        isShapeElement,
        isAudioElement,
        isDisabled,
        isBaseContentVisible,
        element,
        shotToBeHidden,
        allowToggleVisibility,
        getVisibilityButtonText,
        toggleShow,
        isContentProperty,
    } = useShotItem({ shotElement, isToolbarDisabled, isLoading, isDisabledByReview });
    const objectUrl = useObjectUrl(
        element instanceof ImageElement || element instanceof VideoElement ? element?.src : '',
    );
    const previewUrl = useContentPreviewUrl(
        objectUrl || '',
        type,
        element instanceof VideoElement ? element.isAlpha : false,
    );

    if (shotToBeHidden || !element) {
        return null;
    }

    const getThumbnail = () => {
        if (isTextElement || isShapeElement || isAudioElement) {
            return (
                <Thumbnail variant="clean" icon={getIcon(type)} iconColor={token.gray400}>
                    {isContentProperty && <Thumbnail.Indicator backgroundColor="teal500" icon={<IconSync />} />}
                </Thumbnail>
            );
        } else if (isImageElement && element instanceof ImageElement) {
            return (
                <Thumbnail variant="content" imageUrl={element.src || shotElement.properties?.value?.thumbnail}>
                    {isContentProperty && <Thumbnail.Indicator backgroundColor="teal500" icon={<IconSync />} />}
                </Thumbnail>
            );
        }

        return (
            <Thumbnail variant="content" imageUrl={previewUrl}>
                {isContentProperty && <Thumbnail.Indicator backgroundColor="teal500" icon={<IconSync />} />}
            </Thumbnail>
        );
    };

    return (
        <>
            <List>
                <List.Item
                    thumbnail={getThumbnail()}
                    rightElements={
                        allowToggleVisibility ? (
                            <Button isDisabled={isDisabled} variant="clean" onClick={toggleShow}>
                                {getVisibilityButtonText()}
                            </Button>
                        ) : undefined
                    }
                >
                    {shotElement.name}
                </List.Item>
                {isBaseContentVisible && isTextElement && (
                    <TextElement
                        key={id}
                        elementId={id}
                        elementName={shotElement.name}
                        disabled={isDisabled || isDisabledByReview}
                        isLoading={isLoading}
                    />
                )}
                {isBaseContentVisible && !isTextElement && !isShapeElement && (
                    <MediaElement
                        key={id}
                        id={id}
                        type={type as 'image' | 'video' | 'audio'}
                        disabled={isDisabled || isDisabledByReview}
                        isLoading={isLoading}
                    />
                )}
                {isBaseContentVisible && isShapeElement && (
                    <ShapeElement elementId={shotElement.id} disabled={isDisabled || isDisabledByReview} />
                )}
            </List>
            {!isLast && <Inspector.Divider />}
        </>
    );
};

function getIcon(type: ShotElementType) {
    if (type === 'text') {
        return <IconTextField />;
    }

    if (type === 'audio') {
        return <IconMusicLibrary />;
    }

    return <IconBuildShape />;
}

export default ShotItem;
